import {
  appendAuthoredCandidate,
  authoredEndPose,
  authoredSequenceIssue,
  authoredStartPose,
  createAuthoredCandidate,
  createAuthoredCandidateEndingAt,
  getAuthoredDescriptor,
  prependAuthoredCandidate,
  type AuthoredTrackType,
} from '../builder-v2/index.ts';
import { projectPlotBounds } from '../editor/index.ts';
import type { CoasterProject } from '../schema/index.ts';

export interface AuthoredPlacementAvailability {
  readonly allowed: boolean;
  readonly reason: string | null;
}

export function assessAuthoredPlacementAvailability(
  project: CoasterProject,
  descriptorId: string,
  endpointSide: 'beginning' | 'end',
  activeComponentId: string | undefined,
  requestedTrackType: Exclude<AuthoredTrackType, 'station'>,
): AuthoredPlacementAvailability {
  if (project.track.geometryKind !== 'authored_grid_v2') {
    return { allowed: false, reason: 'Authored track pieces are unavailable for this coaster.' };
  }
  const descriptor = getAuthoredDescriptor(descriptorId);
  if (!descriptor) return { allowed: false, reason: 'That track piece is unavailable.' };
  if (descriptor.category === 'station') {
    return project.track.pieces.length === 0
      ? { allowed: true, reason: null }
      : { allowed: false, reason: 'This coaster already has its station.' };
  }
  const sequenceIssue =
    endpointSide === 'end' ? authoredSequenceIssue(project, descriptorId, activeComponentId) : null;
  if (sequenceIssue) return { allowed: false, reason: sequenceIssue };
  const entry =
    endpointSide === 'beginning'
      ? authoredStartPose(project, activeComponentId)
      : authoredEndPose(project, activeComponentId);
  if (!entry) return { allowed: false, reason: 'Place the station first.' };
  const trackType = descriptor.allowedTrackTypes.includes(requestedTrackType)
    ? requestedTrackType
    : 'normal';
  const candidate =
    endpointSide === 'beginning'
      ? createAuthoredCandidateEndingAt(descriptorId, entry, trackType)
      : createAuthoredCandidate(descriptorId, entry, trackType);
  if (!candidate.ok) return { allowed: false, reason: candidate.message };
  let appended =
    endpointSide === 'beginning'
      ? prependAuthoredCandidate(project, candidate.value, activeComponentId)
      : appendAuthoredCandidate(project, candidate.value, activeComponentId);
  if (
    descriptorId === 'straight.1t' &&
    endpointSide === 'end' &&
    !appended.ok &&
    appended.code === 'track_overlap' &&
    appended.message.includes('overlap the station track')
  ) {
    const halfCandidate = createAuthoredCandidate('straight.0_5t', entry, trackType);
    if (halfCandidate.ok) {
      const halfAppended = appendAuthoredCandidate(project, halfCandidate.value, activeComponentId);
      if (halfAppended.ok) appended = halfAppended;
    }
  }
  if (!appended.ok) return { allowed: false, reason: appended.message };
  // Catalog availability runs for every official descriptor whenever the track
  // changes. append/prepend already performs the exact authored connector,
  // sequence and sampled rail-overlap checks. Keep this hot path geometric and
  // reserve the full physics/clearance profile for the one piece the creator
  // actually previews; running a complete Ride Check dozens of times caused
  // increasingly long pauses on larger coasters.
  const bounds = projectPlotBounds(appended.value);
  const baseNodeIds = new Set(project.track.nodes.map((node) => node.id));
  const affectedNodes = appended.value.track.nodes.filter((node) => !baseNodeIds.has(node.id));
  const outside = affectedNodes.find(
    (node) =>
      node.position.x < bounds.minX ||
      node.position.x > bounds.maxX ||
      node.position.z < bounds.minZ ||
      node.position.z > bounds.maxZ,
  );
  if (outside) return { allowed: false, reason: 'That piece would extend outside the build area.' };
  const belowGround = affectedNodes.find((node) => node.position.y < bounds.minY);
  if (belowGround) return { allowed: false, reason: 'That piece would go below ground.' };
  const aboveLimit = affectedNodes.find((node) => node.position.y > bounds.maxY);
  if (aboveLimit) return { allowed: false, reason: 'That piece would exceed the height limit.' };
  return { allowed: true, reason: null };
}
